import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { CraftGlobals } from 'tsconfig/craft-types'
import { useEffect, useContext } from 'react'
import { throttle } from 'lodash'
import {
  CMasthead,
  CMastheadFragments,
  CMastheadBlock,
  CSeomatic,
  SeomaticData,
  CImageCopySlidesFragments,
  CImageCopyBlocksFragments,
  CBackgroundImageListFragments,
  CImageCopySlidesBlock,
  CImageCopyBlocksBlock,
  CImageCopySlides,
  CImageCopyBlocks,
  CBackgroundImageList,
  CBackgroundImageListBlock,
  CTestimonialsSlider,
  CStats,
  CFaqSlider,
  CFindADoctor,
  CCenteredCopy,
  CProcedureSteps,
  CProcedureStepsBlock,
  CProcedureStepsFragments,
  CHero,
  CHeroBlock,
  CHeroFragment,
  CExpandableFaqs,
  CExpandableFaqsFragments,
  CExpandableFaqsBlock,
  CCtaContactFragment,
  CCtaContactBlock,
  CCtaContact,
  CCtaColumnsFragment,
  CCtaColumnsBlock,
  CCtaColumns,
} from 'ui'
import { GetServerSidePropsContext } from 'next'
import { HeaderContext } from 'ui/context/header'
import { gql } from '@urql/core'
import { getSiteInfo, useSiteInfo, serverSideValidateWww } from 'shared'
import { defaultClient } from '../graphql/clients'
import fragments from '../helpers/gql-fragments'
import { getI18nResolvedUrl } from '../helpers/locale-tools'
import DefaultLayout from '../layouts/default'
import getGlobals from '../graphql/get-globals'
import { setCacheHeaders } from '../helpers/cache-config'

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const isWww = serverSideValidateWww({ context })

  if (isWww) {
    return {
      notFound: true,
    }
  }

  const siteInfo = getSiteInfo(context.locale ?? '')
  const isUS = siteInfo.iso === 'en-US'

  const global = await getGlobals(siteInfo.handle)

  const { data } = await defaultClient(siteInfo.handle).query(
    gql`
      query($uri: [String]!, $site: [String]!) {
        entry(uri: $uri, site: $site) {
          ${fragments.seomatic}
          uri
          sectionHandle
          typeHandle
          title
          ... on generalPages_procedure_Entry {
            masthead {
              ...masthead
            }
            imageCopySlides {
              ...imageCopySlides
            }
            imageCopyBlocks {
              ...imageCopyBlocks
            }
            backgroundImageList {
              ...backgroundImageList
            }
            procedureSteps {
              ...procedureSteps
            }
            hero {
              ...hero
            }
            ctaContact {
              ...ctaContact
            }
             ctaColumns {
              ...ctaColumns
            }
            expandableFaqs {
              ...expandableFaqs
            }
          }
        }
      }
      ${CHeroFragment}
      ${CCtaContactFragment}
      ${CCtaColumnsFragment}
      ${CMastheadFragments.field}
      ${CImageCopySlidesFragments.field}
      ${CImageCopyBlocksFragments.field}
      ${CBackgroundImageListFragments.field}
      ${CProcedureStepsFragments.field}
      ${CExpandableFaqsFragments.field}
    `,
    {
      uri: getI18nResolvedUrl(context.req.url ?? ''),
      site: siteInfo.handle,
    }
  )

  if (!data?.entry) {
    return {
      notFound: true,
    }
  }

  setCacheHeaders(context.res)

  return {
    props: {
      ...(await serverSideTranslations(context.locale ?? '', ['common'])),
      entry: data.entry,
      global,
      transparentHeader: !isUS,
    },
  }
}

type Props = {
  entry: {
    seomatic: SeomaticData
    masthead: Array<CMastheadBlock>
    imageCopySlides: Array<CImageCopySlidesBlock>
    imageCopyBlocks: Array<CImageCopyBlocksBlock>
    backgroundImageList: Array<CBackgroundImageListBlock>
    procedureSteps: Array<CProcedureStepsBlock>
    hero: Array<CHeroBlock>
    expandableFaqs: Array<CExpandableFaqsBlock>
    ctaContact: Array<CCtaContactBlock>
    ctaColumns: Array<CCtaColumnsBlock>
  }
  global: CraftGlobals
  transparentHeader: boolean
}

export default function Procedure({ entry, global }: Props) {
  const siteInfo = useSiteInfo()
  const headerContext = useContext(HeaderContext)

  const handleWindowScroll = () => {
    const computeIsClear: boolean = window.pageYOffset === 0
    headerContext?.setIsClear(computeIsClear)
  }

  useEffect(() => {
    if (siteInfo.isUS) {
      headerContext?.setIsClear(false)
    }
  }, [headerContext?.isClear, siteInfo.isUS])

  if (siteInfo.isUS) {
    return (
      <DefaultLayout global={global}>
        <CSeomatic data={entry.seomatic} />
        <CHero field={entry.hero} isProcedure={true} />
        <CProcedureSteps field={entry.procedureSteps} />
        <CExpandableFaqs field={entry.expandableFaqs} />
        <CTestimonialsSlider field={global.testimonialsSlider} />
        <CCtaContact field={entry.ctaContact} />
        <CCtaColumns field={entry.ctaColumns} />
        <CFaqSlider field={global.faqVideoSlider} isProcedure={true} />
        <CCenteredCopy field={global.centeredCopy} />
      </DefaultLayout>
    )
  }
  return (
    <DefaultLayout global={global}>
      <CSeomatic data={entry.seomatic} />
      <CMasthead field={entry.masthead} />
      <CImageCopySlides
        field={entry.imageCopySlides}
        lensBranding={siteInfo.branded}
      />
      <CImageCopyBlocks field={entry.imageCopyBlocks} />
      <CBackgroundImageList field={entry.backgroundImageList} />
      <CTestimonialsSlider field={global.testimonialsSlider} />
      <CStats field={global.stats} />
      <CFaqSlider field={global.faqVideoSlider} />
      <CFindADoctor field={global.findADoctor} />
      <CCenteredCopy field={global.centeredCopy} />
    </DefaultLayout>
  )
}
